import { Image } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import {
	signInWithGooglePopup,
	signInWithFacebookPopup,
	signInWithXPopup,
	signInWithApplePopup,
} from 'utils/firebase.utils';
import {
	logSocialLogin,
	logSocialSignUp,
	useLinkedInCallbaclMutation,
	useSocialLoginMutation,
} from 'services';
import { breakTheName, getBrowserID } from 'utils';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import { useCallback, useEffect, useState } from 'react';
import { LINKED_IN_REDIRECT_URL, LINKED_IN_CLIENT_ID } from 'constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import { setIsLoading } from 'store/common';

const SocialLogin: React.FC<any> = ({ type }) => {
	const { mutateAsync: socialLoginMutation } = useSocialLoginMutation();
	const navigate = useNavigate();
	const [provider, setProvider] = useState('');
	const [profile, setProfile] = useState<any>();
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code');
	const { mutateAsync: linkedInCallbackMutation } = useLinkedInCallbaclMutation();

	const { translate } = useTranslate();

	useEffect(() => {
		if (code?.length && code?.length > 0) {
			setIsLoading(true);
			linkedInCallbackMutation({ code }).then((res: any) => {
				if (res?.success) {
					const userData = res?.data || {};
					if (userData?.user?.isProfileCompleted) {
						setPreviewsRole(res?.data?.user?.userType);
						setUserInfo(res?.data);
					} else {
						navigate('/profile-update', {
							state: {
								data: res,
							},
						});
					}
					setIsLoading(false);
				} else {
					toast.error(translate(res?.message));
					navigate('/login');
					setIsLoading(false);
				}
			});
		}
	}, [code]);

	const handleAnalytics = (event: string) => {
		if (type === 'login') {
			logSocialLogin(event);
		} else {
			logSocialSignUp(event);
		}
	};

	const generateState = (length = 16) => {
		const array = new Uint8Array(length);
		window.crypto.getRandomValues(array);
		return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
	};

	const state = generateState(16); // Protect against CSRF
	const onLogout = useCallback(() => {}, []);

	const loginGoogleuser = async () => {
		handleAnalytics('google');
		const response: any = await signInWithGooglePopup();
		const payload = {
			socialType: 'google',
			socialId: response?._tokenResponse?.localId,
			udid: response?._tokenResponse?.localId,
			firstName: response?._tokenResponse?.firstName,
			lastName: response?._tokenResponse?.lastName,
			email: response?._tokenResponse?.email || response?.user?.email || '',
			userType: 'seeker',
			platform: 'web',
			deviceToken: getBrowserID(),
			voipDeviceToken: '',
		};

		socialLogin(payload);
	};

	const socialLogin = (payload: any) => {
		socialLoginMutation(payload).then((result: Record<string, any>) => {
			if (result?.success) {
				const userData = result?.data || {};
				if (userData?.user?.isProfileCompleted) {
					setPreviewsRole(result?.data?.user?.userType);
					setUserInfo(result?.data);
				} else {
					navigate('/profile-update', {
						state: {
							data: result,
						},
					});
				}
			} else {
				toast.error(translate(result?.message));
				navigate('/login');
			}
		});
	};

	const loginFacebookUser = async () => {
		handleAnalytics('facebook');
		const response: any = await signInWithFacebookPopup();
		const {
			user: { email = '', displayName: name, uid, providerData },
		} = response;
		const [firstName, lastName] = breakTheName(name || 'New User');
		const payload = {
			socialType: 'facebook',
			socialId: uid,
			firstName: firstName || ' ',
			email: email || providerData?.[0]?.email || '',
			udid: uid,
			lastName: lastName || ' ',
			userType: 'seeker',
			platform: 'web',
			deviceToken: getBrowserID(),
			voipDeviceToken: '',
		};
		socialLogin(payload);
	};
	const loginTwitterUser = async () => {
		handleAnalytics('twitter');
		try {
			const response: any = await signInWithXPopup();

			const {
				user: { email = '', displayName: name, uid, providerData },
			} = response;

			const [firstName, lastName] = breakTheName(name || 'New User');
			const payload = {
				socialType: 'twitter',
				socialId: uid,
				firstName: firstName || ' ',
				email: email || providerData?.[0]?.email || '',
				udid: uid,
				lastName: lastName || ' ',
				userType: 'seeker',
				platform: 'web',
				deviceToken: getBrowserID(),
				voipDeviceToken: '',
			};

			socialLogin(payload);
		} catch (error: any) {
			console.error('Error during Twitter login:', error);
		}
	};

	const linkedInLogin = () => {
		handleAnalytics('linkedin');
		const scope = 'openid,profile,w_member_social,email';
		const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKED_IN_CLIENT_ID}&redirect_uri=${encodeURIComponent(LINKED_IN_REDIRECT_URL)}&scope=${encodeURIComponent(scope)}&state=${state}`;
		window.location.href = linkedInAuthUrl;
	};

	const loginAppleUser = async () => {
		handleAnalytics('apple');
		try {
			const response: any = await signInWithApplePopup();
			const {
				user: { email = '', displayName: name, uid, providerData },
			} = response;

			// Apple might not always return the name
			const [firstName, lastName] = breakTheName(name || 'New User');

			// Check if this is a private relay email
			const isPrivateEmail = providerData?.some(
				(provider: any) =>
					provider.providerId === 'apple.com' &&
					provider.email?.endsWith('@privaterelay.appleid.com'),
			);

			// Format the email display
			const displayedEmail = isPrivateEmail ? `${email}` : email;

			const payload = {
				socialType: 'apple',
				socialId: uid,
				firstName: firstName || ' ',
				email: displayedEmail || '',
				udid: uid,
				lastName: lastName || ' ',
				userType: 'seeker',
				platform: 'web',
				deviceToken: getBrowserID(),
				voipDeviceToken: '',
			};

			socialLogin(payload);
		} catch (error: any) {
			console.error('Error during Apple login:', error);
		}
	};

	return (
		<div className="social-links d-flex justify-content-between">
			<div
				onClick={loginFacebookUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.FacebookIcon} alt="FacebookIcon" loading="lazy" />
			</div>
			<div
				onClick={loginGoogleuser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.GoogleIcon} alt="GoogleIcon" loading="lazy" />
			</div>

			<div
				onClick={loginAppleUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.AppleIcon} alt="AppleIcon" loading="lazy" />
			</div>

			<div
				className="social-links-box rounded-pill"
				onClick={linkedInLogin}
				style={{ cursor: 'pointer' }}
			>
				{provider && profile && (
					<div className="card">
						<div className="avt">
							<img alt="141" src={profile?.avatar} loading="lazy" />
						</div>

						<h3 className="provider">{provider?.toUpperCase()}</h3>

						<div className="content">
							<div className="data">
								{Object.entries(profile).map(([key, value]: any) => (
									<div className="field" key={key}>
										<div className="label">{key}: </div>
										<div className="value">{JSON.stringify(value)}</div>
									</div>
								))}
							</div>
							<button className="btnLogout" onClick={onLogout}>
								{translate('Header.logout')}
							</button>
						</div>
					</div>
				)}
				<Image src={IMAGE_PATHS.LinkedinIcon} alt="LinkedinIcon" loading="lazy" />
			</div>

			<div
				onClick={loginTwitterUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.TwitterIcon} alt="TwitterIcon" loading="lazy" />
			</div>
		</div>
	);
};

export default SocialLogin;
